import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
  AddressInputField,
  Button,
  DropDownInput,
  InputField,
  UploadInput,
} from "@/components";
import { IoArrowForward } from "react-icons/io5";
import { statesOptionsArray } from "../../../utils/Data.js";
import { gstDetailsSchema } from "../../../schemas";
import { useFieldError, useValidation } from "@/";
import { BiSolidEditAlt } from "react-icons/bi";
import PhotoViewProvider from "../../../components/PhotoViewProvider.jsx";

const GstDetails = () => {
  const {
    handleNextStep,
    handlePreviousStep,
    handleSkipStep,
    handleIsEdit,
    formData,
    setFormData,
    isDisabled,
    handleIsCancel,
    kycStatus,
    skippedSteps,
  } = useOutletContext();
  const { validate, errors } = useValidation(gstDetailsSchema);
  const [formErrors, setFormErrors] = useState(errors);
  const getErrorMessage = useFieldError(formErrors || []);
  const status = kycStatus;
  // Helper functions to update gstDetails in parent's state
  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      businessDetails: {
        ...prev?.businessDetails,
        [field]: value,
      },
    }));
  };

  // For nested address fields in businessDetails.address
  const handleAddressChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      businessDetails: {
        ...prev?.businessDetails,
        address: {
          ...prev?.businessDetails.address,
          [field]: value,
        },
      },
    }));
  };

  // For file uploads at the top level
  const handleUploadChange = (field, file) => {
    setFormData((prev) => ({
      ...prev,
      kycDocuments: {
        ...prev?.kycDocuments,
        [field]: file,
      },
    }));
  };

  // Options for Business Organization Type dropdown
  const BusinessOrganizationTypes = {
    Type1: "Sole Proprietorship",
    Type2: "Partnership Firm",
    Type3: "Limited Liability Partnership (LLP)",
    Type4: "Private Limited Company",
    Type5: "Public Limited Company",
    Type6: "One Person Company (OPC)",
    Type7: "Cooperative Society",
    Type8: "Joint Hindu Family Business",
    Type9: "Section 8 Company",
    Type10: "Trusts",
    Type11: "Non-Banking Financial Companies (NBFCs)",
    Type12: "Franchise Business",
  };

  const businessOrganizationOptions = Object.keys(
    BusinessOrganizationTypes
  ).map((key) => ({
    label: BusinessOrganizationTypes[key],
    value: BusinessOrganizationTypes[key],
  }));

  const handleEdit = () => {
    handleIsEdit();
  };

  const previous = () => {
    handlePreviousStep();
  };

  const handleNext = () => {
    if (isDisabled) {
      if (skippedSteps.has(2)) {
        handleSkipStep();
        return;
      }
      handleNextStep();
      return;
    }

    setFormErrors([]);

    const data = {
      ...formData.businessDetails,
    };

    const result = validate(data);

    if (!result.isValid) {
      console.log("Validation errors:", result.errors);
      setFormErrors(result.errors);
      return;
    }

    console.log("GST data:", result.data);

    handleNextStep();
  };
  const handleCancel = () => {
    handleIsCancel();
  };
  return (
    <div className="flex flex-col gap-4 md:gap-4 w-full p-2">
      <h2 className="md:text-headline3 text-headline4 font-nunito text-secondary-90 font-bold">
        GST Details
      </h2>
      <form
        onSubmit={(e) => e.preventDefault()}
        className="flex flex-col gap-3 w-full "
      >
        <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start">
          <InputField
            label="Firm Name"
            type="text"
            placeholder="Enter firm name"
            value={formData.businessDetails?.firmName || ""}
            onChange={(val) => handleChange("firmName", val)}
            error={getErrorMessage("firmName")}
            disabled={isDisabled}
          />
          <InputField
            label="Firm Phone No."
            type="number"
            placeholder="Enter firm phone number"
            value={
              formData.businessDetails?.phone?.phoneNumber ||
              formData.businessDetails?.phone ||
              ""
            }
            onChange={(val) => handleChange("phone", val)}
            maxLength={10}
            error={getErrorMessage("phone")}
            disabled={isDisabled}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start">
          <InputField
            label="Pincode"
            type="number"
            placeholder="Enter pincode"
            value={formData.businessDetails?.address?.pincode || ""}
            onChange={(val) => handleAddressChange("pincode", val)}
            error={getErrorMessage("address.pincode")}
            disabled={isDisabled}
          />
          <DropDownInput
            label="State"
            value={formData.businessDetails?.address?.state || ""}
            onChange={(value) => handleAddressChange("state", value)}
            placeholder="Select your state"
            options={statesOptionsArray}
            error={getErrorMessage("address.state")}
            disabled={isDisabled}
          />
          <InputField
            label="City"
            type="text"
            placeholder="Enter city"
            value={formData.businessDetails?.address?.city || ""}
            onChange={(val) => handleAddressChange("city", val)}
            error={getErrorMessage("address.city")}
            disabled={isDisabled}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start">
          <AddressInputField
            label="Address"
            placeholder="Enter address"
            value={
              formData.businessDetails?.address?.lines?.[0]?.line1 ||
              formData.businessDetails?.address?.line1 ||
              ""
            }
            onChange={(val) => handleAddressChange("line1", val)}
            error={getErrorMessage("address.line1")}
            disabled={isDisabled}
          />
          <DropDownInput
            label="Business Organization Type"
            value={formData.businessDetails?.businessType || ""}
            onChange={(value) => handleChange("businessType", value)}
            placeholder="Select your business type"
            options={businessOrganizationOptions}
            error={getErrorMessage("businessType")}
            disabled={isDisabled}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start">
          <InputField
            label="GST No."
            type="text"
            placeholder="Enter GST No."
            value={formData.businessDetails?.gstNumber || ""}
            onChange={(val) => handleChange("gstNumber", val)}
            error={getErrorMessage("gstNumber")}
            disabled={isDisabled}
          />
          <InputField
            label="Firm PAN No."
            type="text"
            placeholder="Enter Firm PAN No."
            value={formData.businessDetails?.firmPanNumber || ""}
            onChange={(val) => handleChange("firmPanNumber", val)}
            error={getErrorMessage("firmPanNumber")}
            disabled={isDisabled}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-4 items-start">
          {isDisabled ? (
            <PhotoViewProvider
              imageURL={formData?.kycDocuments?.firmGST || ""}
              label="GST Registration Certificate"
            />
          ) : (
            <UploadInput
              label="GST Registration Certificate"
              placeholder="Upload registration certificate."
              value={formData?.kycDocuments?.firmGST || null}
              onChange={(val) => handleUploadChange("firmGST", val)}
              note="Note: File type should only be pdf, png, jpeg and should be in color"
              noteClassName="text-caption md:text-body1 pr-2"
              spanClassName="text-caption md:text-body1"
              error={getErrorMessage("firmGST")}
              disabled={isDisabled}
            />
          )}

          {isDisabled ? (
            <PhotoViewProvider
              imageURL={formData?.kycDocuments?.firmPan || ""}
              label="Firm PAN Card"
            />
          ) : (
            <UploadInput
              label="Firm PAN Card"
              placeholder="Upload Firm PAN Card."
              value={formData?.kycDocuments?.firmPan || null}
              onChange={(val) => handleUploadChange("firmPan", val)}
              note="Note: File type should only be pdf, png, jpeg and should be in color"
              noteClassName="text-caption md:text-body1 pr-2"
              spanClassName="text-caption md:text-body1"
              error={getErrorMessage("firmPan")}
              disabled={isDisabled}
            />
          )}
        </div>

        <div className="flex gap-4 w-full md:w-1/2">
          {isDisabled ? (
            <PhotoViewProvider
              imageURL={formData?.kycDocuments?.firmLOGO}
              label="Firm Logo"
            />
          ) : (
            <UploadInput
              label="Firm Logo"
              placeholder="Upload Firm Logo."
              value={formData?.kycDocuments?.firmLogo || null}
              onChange={(val) => handleUploadChange("firmLOGO", val)}
              note="Note: File type should only be pdf, png, jpeg and should be in color"
              noteClassName="text-caption md:text-body1 pr-2"
              spanClassName="text-caption md:text-body1"
              error={getErrorMessage("firmLOGO")}
              disabled={isDisabled}
            />
          )}
        </div>
      </form>
      <div className="flex flex-row gap-1 md:gap-2 justify-between sm:justify-end items-end">
        <div className="flex gap-1 md:gap-2 mt-8 mr-2 items-end">
          <Button
            size="sm"
            variant="default"
            onClick={isDisabled ? handleEdit : handleCancel}
            icon={BiSolidEditAlt}
          >
            {isDisabled ? "Edit" : "Cancel"}
          </Button>
          <Button size="sm" variant="outline" onClick={previous}>
            Previous
          </Button>
          {!isDisabled && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleSkipStep}
              className="min-w-16"
            >
              Skip
            </Button>
          )}
        </div>
        <Button
          size="sm"
          variant="default"
          onClick={handleNext}
          icon={IoArrowForward}
          className="h-fit"
        >
          {isDisabled ? "Next" : "Save And Next"}
        </Button>
      </div>
    </div>
  );
};

export default GstDetails;
